import {
  type CategoriesSearch,
  type CounterpartiesSearch,
  type DocumentsSearch,
  parseCategoriesSearch,
  parseCounterpartiesSearch,
  parseDocumentsSearch,
  parseExportsSearch,
  parseReconciliationSearch,
  parseRulesSearch,
  parseTransactionsSearch,
  type ReconciliationSearch,
  serializeExportsSearch,
  serializeReconciliationSearch,
  serializeTransactionsSearch,
  type TransactionsSearch,
} from '@observatory/ui-url-state';
import type { Session } from '@supabase/supabase-js';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  type Router,
  type RouterHistory,
  redirect,
  type SearchMiddleware,
} from '@tanstack/react-router';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; session: Session };

interface RouterContext {
  auth: AuthState;
}

// On every navigate TanStack Router writes the *validated* search back into
// the URL — its validateSearch middleware merges the parsed state over the
// params it was given — and its default stringifier JSON-encodes whatever
// is not a string: ?period={"kind":"year","year":"2026"}&invalidPeriod=null.
// This middleware hands the URL the route's own query form instead, so a
// Period is written ?period=2026-03 | 2026-Q2 | 2026 or ?from=&to=
// (ADR-0021 §1). It re-parses the merged object, so each route's parser
// must read its own parsed state back unchanged; components still get the
// parsed state from useSearch.
function canonicalSearch<S>(
  parse: (raw: Record<string, unknown>) => S,
  serialize: (state: S) => Record<string, unknown>,
): SearchMiddleware<S> {
  // The router types a middleware over the parsed state; what flows
  // through it at runtime are query params, which is what this returns.
  return ({ search, next }) =>
    serialize(parse(next(search) as unknown as Record<string, unknown>)) as unknown as S;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-in') {
      throw redirect({ to: '/transactions', search: {} as never });
    }
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-in') {
      throw redirect({ to: '/transactions', search: {} as never });
    }
  },
  component: lazyRouteComponent(() => import('./login-page.tsx'), 'LoginPage'),
});

// /inbox is retained as a redirect-only route so any deep-linked or
// bookmarked URLs from older builds land on /transactions instead of 404.
const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inbox',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
    throw redirect({ to: '/transactions', search: {} as never });
  },
});

const counterpartiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/counterparties',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: (search: Record<string, unknown>): CounterpartiesSearch =>
    parseCounterpartiesSearch(search),
  component: lazyRouteComponent(() => import('./counterparties-route.tsx'), 'CounterpartiesRoute'),
});

const categoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/categories',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: (search: Record<string, unknown>): CategoriesSearch =>
    parseCategoriesSearch(search),
  component: lazyRouteComponent(() => import('./categories-route.tsx'), 'CategoriesRoute'),
});

const documentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: (search: Record<string, unknown>): DocumentsSearch =>
    parseDocumentsSearch(search),
  component: lazyRouteComponent(() => import('./documents-route.tsx'), 'DocumentsRoute'),
});

// /settings is a parent path that redirects to the canonical first tab
// (Profile). Every nested `/settings/<tab>` URL is auth-guarded the same
// way as /inbox.
const settingsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
    throw redirect({ to: '/settings/$tab', params: { tab: 'profile' } });
  },
});

const settingsTabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/$tab',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
  component: lazyRouteComponent(() => import('./settings-route.tsx'), 'SettingsRoute'),
});

// /exports — Agenda CSV download composer (ui-spec §10.12, issue #24).
const exportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/exports',
  validateSearch: (raw) => parseExportsSearch(raw as Record<string, unknown>),
  search: { middlewares: [canonicalSearch(parseExportsSearch, serializeExportsSearch)] },
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
  component: lazyRouteComponent(() => import('./exports-route.tsx'), 'ExportsRoute'),
});

const rulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rules',
  validateSearch: (search) => parseRulesSearch(search as Record<string, unknown>),
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
  component: lazyRouteComponent(() => import('./rules-route.tsx'), 'RulesRoute'),
});

const transactionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transactions',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: (raw: Record<string, unknown>): TransactionsSearch =>
    parseTransactionsSearch(raw as Record<string, string | undefined>),
  search: {
    middlewares: [canonicalSearch(parseTransactionsSearch, serializeTransactionsSearch)],
  },
  component: lazyRouteComponent(() => import('./transactions-route.tsx'), 'TransactionsRoute'),
});

const reconciliationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reconciliation',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: (raw: Record<string, unknown>): ReconciliationSearch =>
    parseReconciliationSearch(raw),
  search: {
    middlewares: [canonicalSearch(parseReconciliationSearch, serializeReconciliationSearch)],
  },
  component: lazyRouteComponent(() => import('./reconciliation-route.tsx'), 'ReconciliationRoute'),
});

const skillsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/skills',
  beforeLoad: ({ context }) => {
    if (context.auth.status === 'signed-out') {
      throw redirect({ to: '/login' });
    }
  },
  component: lazyRouteComponent(() => import('./skills-route.tsx'), 'SkillsRoute'),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  inboxRoute,
  counterpartiesRoute,
  categoriesRoute,
  documentsRoute,
  settingsIndexRoute,
  settingsTabRoute,
  exportsRoute,
  rulesRoute,
  transactionsRoute,
  reconciliationRoute,
  skillsRoute,
]);

export type AppRouter = Router<typeof routeTree, 'never', false>;

export function createAppRouter(options: { auth: AuthState; history?: RouterHistory }): AppRouter {
  return createRouter({
    routeTree,
    history: options.history,
    context: { auth: options.auth },
    defaultPreload: false,
  }) as AppRouter;
}

export const routes = {
  inboxRoute,
  loginRoute,
  counterpartiesRoute,
  categoriesRoute,
  documentsRoute,
  settingsTabRoute,
  exportsRoute,
  rulesRoute,
  transactionsRoute,
  reconciliationRoute,
  skillsRoute,
};

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
